// Providers
import { getIconBasedOnEventType, getLabelValue } from '@sportal365/smp-widget-utils';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';

// Types
import { BaseWidgetProps, EventTypeCode, IFootballTimelineConfig, ParticipantType } from '../../../../../packages/types';

import useTimeline from '../../hooks/useTimeline';
import { EventIcon } from '../Lineups/EventIcons';
import {
  EventInfoWrapper,
  GeneralTabInfo,
  InjuryTimeInfo,
  PrimaryInfo,
  SecondaryInfo,
  TimelineEventInfo,
  TimelineWidgetWrapper,
  TimelineWidgetWrapperEmptyState,
} from './EventInfo';
import { ConfigContext } from '@velinsi/smp-widgets-ui';
import {
  getGeneralEventLabel,
  getNumberCard,
  isEventForSkip,
  isGeneralEvent,
  reverseByTeamPosition,
  shouldShowMinutes,
  showMinutes,
} from '../../helpers/timeline';

export type TimelineWidgetProps = BaseWidgetProps<IFootballTimelineConfig>;

export const TimelineWidget = ({ themes, config, sdkOptions, shadow }: TimelineWidgetProps) => {
  const {
    widgetData: { timeline, isLoading },
  } = useTimeline({
    dataMatchId: config?.dataMatchId,
    dataRefreshTime: config?.dataRefreshTime,
    sdkOptions,
  });

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider
        slickStyles={true}
        shadow={shadow}
        themes={themes}
        dataTheme={config?.dataTheme}
        isLoading={isLoading}
        css={{ backgroundColor: '$timelineBackgroundColor', position: 'relative' }}
      >
        {timeline.length > 0 ? (
          <TimelineWidgetWrapper>
            {timeline
              .filter((ev) => !isEventForSkip(ev.type_code))
              .map((event, index) => {
                const timelineInfoContainerStyles: React.CSSProperties = {
                  width: 'calc(50% - 8px - 16px)', // event icon width / 2; padding
                  textAlign: event.team_position === ParticipantType.AWAY ? 'right' : 'left',
                  padding: '0 16px',
                };

                if (isGeneralEvent(event.type_code)) {
                  return <GeneralTabInfo key={event.type_code}>{getGeneralEventLabel(event.type_code, config?.dataLabels)}</GeneralTabInfo>;
                }

                if (event.type_code === EventTypeCode.INJURY_TIME) {
                  return (
                    <InjuryTimeInfo key={event.type_code}>
                      <span>
                        +{event.injury_time_minutes} {getLabelValue(config?.dataLabels?.minutes, 'min')}
                      </span>
                    </InjuryTimeInfo>
                  );
                }

                return (
                  <EventInfoWrapper key={event.id} style={{ flexDirection: reverseByTeamPosition(event) }}>
                    <div
                      style={{
                        ...timelineInfoContainerStyles,
                        color: 'var(--colors-secondaryHeaderTextColor)',
                        height: '32px',
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: reverseByTeamPosition(event) === 'row' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      {shouldShowMinutes(event) && showMinutes(event)}
                    </div>
                    <div style={{ width: '32px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <EventIcon
                        key={event.type_code}
                        style={{
                          backgroundImage: `url("${getIconBasedOnEventType(event?.type_code, config?.dataTheme)}")`,
                          width: '24px',
                          height: '24px',
                          backgroundPosition: 'center',
                        }}
                      />
                    </div>
                    {getNumberCard(timeline, index)}
                    <TimelineEventInfo
                      event={event}
                      customStyles={{ ...timelineInfoContainerStyles, flexDirection: reverseByTeamPosition(event) }}
                    />
                  </EventInfoWrapper>
                );
              })}
          </TimelineWidgetWrapper>
        ) : (
          <>
            <TimelineWidgetWrapperEmptyState style={{ minHeight: '107px' }}></TimelineWidgetWrapperEmptyState>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '12px' }}>
              {config?.widgetStartTime ? (
                <>
                  <PrimaryInfo style={{ width: 'unset' }}>{getLabelValue(config?.dataLabels?.game_starts, 'Game starts')}</PrimaryInfo>
                  &nbsp;<SecondaryInfo style={{ width: 'unset' }}>{config.widgetStartTime}</SecondaryInfo>
                </>
              ) : (
                <PrimaryInfo style={{ width: 'unset' }}>{getLabelValue(config?.dataLabels?.no_events_yet, 'No events yet')}</PrimaryInfo>
              )}
            </div>
          </>
        )}
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
